import React, { useState, useRef, useEffect, useContext } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { cn } from '@/shared/utils/cn';

// Helper function to check if a value is considered "empty" or "unselected"
const isValueEmpty = (value: any): boolean => {
  return value === undefined || value === null || value === '';
};

// Helper function to check if a value is valid (not empty)
const isValidValue = (value: any): boolean => {
  return !isValueEmpty(value);
};

// Helper function to safely compare values (handles string vs number comparison)
const valuesEqual = (a: any, b: any): boolean => {
  if (a === b) return true;
  if (a == b) return true; // Loose equality for string/number comparison
  return false;
};

interface SelectProps {
  children: React.ReactNode;
  value?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  className?: string;
}

interface SelectTriggerProps {
  children: React.ReactNode;
  className?: string;
}

interface SelectValueProps {
  placeholder?: string;
  className?: string;
}

interface SelectContentProps {
  children: React.ReactNode;
  className?: string;
}

interface SelectItemProps {
  value: string;
  children: React.ReactNode;
  disabled?: boolean;
  className?: string;
}

// Context for select state
const SelectContext = React.createContext<{
  value?: string;
  onValueChange?: (value: string) => void;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  disabled?: boolean;
  selectedLabel?: string;
  setSelectedLabel?: (label: string) => void;
}>({
  value: undefined,
  onValueChange: undefined,
  isOpen: false,
  setIsOpen: () => {},
  disabled: false,
  selectedLabel: undefined,
  setSelectedLabel: () => {},
});

const Select: React.FC<SelectProps> = ({
  children,
  value,
  onValueChange,
  disabled = false,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<string>('');

  // Función para encontrar el label del valor actual
  const findLabelForValue = React.useCallback(
    (currentValue: string | undefined): string => {
      if (isValueEmpty(currentValue)) return '';

      // Buscar en los children para encontrar el SelectItem con el valor actual
      const findLabelInChildren = (children: React.ReactNode): string => {
        return React.Children.toArray(children).reduce(
          (label: string, child: any) => {
            if (label) return label; // Si ya encontramos el label, no seguir buscando

            // Verificar si es un SelectItem
            if (
              child?.props?.value &&
              valuesEqual(child.props.value, currentValue)
            ) {
              return typeof child.props.children === 'string'
                ? child.props.children
                : React.Children.toArray(child.props.children).join('');
            }

            // Buscar recursivamente en children
            if (child?.props?.children) {
              const found = findLabelInChildren(child.props.children);
              if (found) return found;
            }

            return label;
          },
          ''
        );
      };

      return findLabelInChildren(children);
    },
    [children]
  );

  // Efecto para sincronizar selectedLabel con el value inicial y cambios
  React.useEffect(() => {
    if (isValidValue(value)) {
      const label = findLabelForValue(value);
      if (label) {
        setSelectedLabel(label);
      }
    } else {
      setSelectedLabel('');
    }
  }, [value, findLabelForValue]);

  return (
    <SelectContext.Provider
      value={{
        value,
        onValueChange,
        isOpen,
        setIsOpen,
        disabled,
        selectedLabel,
        setSelectedLabel,
      }}
    >
      <div className={cn('relative', className)}>{children}</div>
    </SelectContext.Provider>
  );
};

const SelectTrigger: React.FC<SelectTriggerProps> = ({
  children,
  className,
}) => {
  const { isOpen, setIsOpen, disabled } = useContext(SelectContext);

  const handleClick = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={disabled}
      className={cn(
        'flex w-full items-center justify-between rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm',
        'placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-500',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-gray-50',
        isOpen && 'ring-2 ring-primary-200 border-primary-500',
        className
      )}
    >
      {children}
      <ChevronDown
        className={cn(
          'h-4 w-4 opacity-50 transition-transform',
          isOpen && 'rotate-180'
        )}
      />
    </button>
  );
};

const SelectValue: React.FC<SelectValueProps> = React.memo(
  ({ placeholder, className }) => {
    const { value, selectedLabel } = useContext(SelectContext);

    return (
      <span
        className={cn(
          'block truncate',
          isValueEmpty(value) && 'text-gray-500',
          className
        )}
      >
        {selectedLabel || placeholder}
      </span>
    );
  }
);

const SelectContent: React.FC<SelectContentProps> = ({
  children,
  className,
}) => {
  const { isOpen, setIsOpen } = useContext(SelectContext);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        contentRef.current &&
        !contentRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, setIsOpen]);

  if (!isOpen) return null;

  return (
    <div
      ref={contentRef}
      className={cn(
        'absolute top-full left-0 right-0 z-50 mt-1 max-h-60 overflow-auto rounded-md border bg-white shadow-lg',
        className
      )}
    >
      <div className="p-1">{children}</div>
    </div>
  );
};

const SelectItem: React.FC<SelectItemProps> = ({
  value,
  children,
  disabled = false,
  className,
}) => {
  const {
    value: selectedValue,
    onValueChange,
    setIsOpen,
    setSelectedLabel,
  } = useContext(SelectContext);
  const isSelected =
    isValidValue(selectedValue) && valuesEqual(selectedValue, value);

  const handleClick = () => {
    if (!disabled && onValueChange) {
      onValueChange(value);
      // Extraer el texto del children para setSelectedLabel
      const labelText =
        typeof children === 'string'
          ? children
          : React.Children.toArray(children).join('');
      setSelectedLabel?.(labelText);
      setIsOpen(false);
    }
  };

  return (
    <div
      onClick={handleClick}
      className={cn(
        'relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors',
        'hover:bg-gray-100 focus:bg-gray-100',
        disabled && 'opacity-50 cursor-not-allowed',
        isSelected && 'bg-blue-50 text-blue-600',
        className
      )}
    >
      {isSelected && <Check className="mr-2 h-4 w-4" />}
      {children}
    </div>
  );
};

export { Select, SelectContent, SelectItem, SelectTrigger, SelectValue };
export default Select;
